// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import { applyPenders } from 'lib/common';
import * as TaxInvoiceAPI from 'lib/api/me/taxInvoice';

/* ACTION TYPE */
const GET_TAX_INVOICE_LIST = 'taxInvoice/GET_TAX_INVOICE_LIST';
const GET_TAX_INVOICE_BY_ORDER = 'taxInvoice/GET_TAX_INVOICE_BY_ORDER';
const GET_TAX_INVOICE_BY_KEY = 'taxInvoice/GET_TAX_INVOICE_BY_KEY';
const SET_SELECTED_TAX_INVOICE = 'taxInvoice/SET_SELECTED_TAX_INVOICE';
const CLEAR_SELECTED_TAX_INVOICE = 'taxInvoice/CLEAR_SELECTED_TAX_INVOICE';

/* ACTION CREATOR */
const getTaxInvoiceList = createAction(
  GET_TAX_INVOICE_LIST,
  TaxInvoiceAPI.getTaxInvoiceList,
);
const getTaxInvoiceByOrder = createAction(
  GET_TAX_INVOICE_BY_ORDER,
  TaxInvoiceAPI.getTaxInvoiceByOrder,
);
const getTaxInvoiceByKey = createAction(
  GET_TAX_INVOICE_BY_KEY,
  TaxInvoiceAPI.getTaxInvoiceByKey,
);
const setSelectedTaxInvoice = createAction(SET_SELECTED_TAX_INVOICE);
const clearSelectedTaxInvoice = createAction(CLEAR_SELECTED_TAX_INVOICE);

/* ACTION FLOW TYPE */
type GetTaxInvoiceListAction = ActionType<typeof getTaxInvoiceList>;
type GetTaxInvoiceByOrderAction = ActionType<typeof getTaxInvoiceByOrder>;
type GetTaxInvoiceByKeyAction = ActionType<typeof getTaxInvoiceByKey>;
type SetSelectedTaxInvoiceAction = ActionType<typeof setSelectedTaxInvoice>;
type ClearSelectedTaxInvoiceAction = ActionType<typeof clearSelectedTaxInvoice>;

/* ACTION CREATORS INTERFACE */
export interface TaxInvoiceActionCreators {
  getTaxInvoiceList(params?: any): any;
  getTaxInvoiceByOrder(orderId: string): any;
  getTaxInvoiceByKey(issuanceKey: string): any;
  setSelectedTaxInvoice(invoice: any): any;
  clearSelectedTaxInvoice(): any;
}

/* EXPORT ACTION CREATORS */
export const actionCreators: TaxInvoiceActionCreators = {
  getTaxInvoiceList,
  getTaxInvoiceByOrder,
  getTaxInvoiceByKey,
  setSelectedTaxInvoice,
  clearSelectedTaxInvoice,
};

/* STATE TYPES */
export type TaxInvoice = {
  list: Array<any>,
  selectedTaxInvoice: any,
  loading: boolean,
  error: string | null,
};

/* INITIAL STATE */
const initialState: TaxInvoice = {
  list: [],
  selectedTaxInvoice: null,
  loading: false,
  error: null,
};

/* REDUCER */
const reducer = handleActions(
  {
    [GET_TAX_INVOICE_LIST]: (state, action: GetTaxInvoiceListAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.list = action.payload || [];
        draft.loading = false;
        draft.error = null;
      });
    },
    [GET_TAX_INVOICE_BY_ORDER]: (state, action: GetTaxInvoiceByOrderAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.selectedTaxInvoice = (action.payload && action.payload.taxInvoice) || null;
        draft.loading = false;
        draft.error = null;
      });
    },
    [GET_TAX_INVOICE_BY_KEY]: (state, action: GetTaxInvoiceByKeyAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.selectedTaxInvoice = (action.payload && action.payload.taxInvoice) || null;
        draft.loading = false;
        draft.error = null;
      });
    },
    [SET_SELECTED_TAX_INVOICE]: (state, action: SetSelectedTaxInvoiceAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.selectedTaxInvoice = action.payload;
      });
    },
    [CLEAR_SELECTED_TAX_INVOICE]: (state, action: ClearSelectedTaxInvoiceAction) => {
      return produce(state, draft => {
        draft.selectedTaxInvoice = null;
      });
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: GET_TAX_INVOICE_LIST,
    onPending: state =>
      produce(state, draft => {
        draft.loading = true;
        draft.error = null;
      }),
    onSuccess: (state, action) =>
      produce(state, draft => {
        draft.list = (action.payload && action.payload.data) || [];
        draft.loading = false;
        draft.error = null;
      }),
    onFailure: (state, action) =>
      produce(state, draft => {
        draft.loading = false;
        draft.error =
          (action.payload && action.payload.message) || '세금계산서 목록을 불러오는데 실패했습니다.';
      }),
  },
  {
    type: GET_TAX_INVOICE_BY_ORDER,
    onPending: state =>
      produce(state, draft => {
        draft.loading = true;
        draft.error = null;
      }),
    onSuccess: (state, action) =>
      produce(state, draft => {
        draft.selectedTaxInvoice = (action.payload && action.payload.data && action.payload.data.taxInvoice) || null;
        draft.loading = false;
        draft.error = null;
      }),
    onFailure: (state, action) =>
      produce(state, draft => {
        draft.loading = false;
        draft.error = (action.payload && action.payload.message) || '세금계산서 조회에 실패했습니다.';
      }),
  },
  {
    type: GET_TAX_INVOICE_BY_KEY,
    onPending: state =>
      produce(state, draft => {
        draft.loading = true;
        draft.error = null;
      }),
    onSuccess: (state, action) =>
      produce(state, draft => {
        draft.selectedTaxInvoice = (action.payload && action.payload.data && action.payload.data.taxInvoice) || null;
        draft.loading = false;
        draft.error = null;
      }),
    onFailure: (state, action) =>
      produce(state, draft => {
        draft.loading = false;
        draft.error = (action.payload && action.payload.message) || '세금계산서 조회에 실패했습니다.';
      }),
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/taxInvoice.js